/* eslint-disable react-hooks/exhaustive-deps */
import { Column } from '@access-erp/common';
import {
  CellClickedEvent,
  CellType,
  FilterValueTypes,
  StringFilterOption,
  ValueFormatterParams,
} from '@access-erp/ui-components';
import { useMemo } from 'react';

import { renderUploadedBy } from '@/components/common/EnquiriesScreen/customFilters/UploadedByMultiSelect';
import {
  createLinkCellRenderer,
  shouldSkipCellClickNavigation,
} from '@/components/common/EnquiriesScreen/components/LinkCellRenderer';
import { CellFormatters } from '@/components/common/EnquiriesScreen/utils/CellFormatters';
import { filtersforStringifiedNumbers } from '@/components/common/EnquiriesScreen/utils/filtersForStringigfiedNumbers';
import { UsageHeaderOutput, VarBillingUsageHeaderStatusEnum } from '@/graphql/contracts/_generated_/contracts';
import { guidFilterOptions } from '@/pages/SalesOrders/columns/filters.config';
import { browserHistory } from '@/routing/browserHistory';
import { DataMappers } from '@/utils/DataMappers';
import { getParameterizedLink } from '@/utils/getLinkWithSubscriptionId.utils';
import { PreloadData } from '@/utils/microfrontend/preloadData';

export const useHeaderColumns = () => {
  const usageHeaderStatuses = useMemo(() => PreloadData.usageHeaderStatuses(), []);

  const goToUsageRecords = (documentId: string, searchString?: string) => {
    let link = `/variable-billing/${documentId}`;

    if (searchString) {
      link += `?search=${searchString}`;
    }
    return getParameterizedLink(link);
  };

  const mapEnumToFormattedStatus = (value?: VarBillingUsageHeaderStatusEnum | null) => {
    return usageHeaderStatuses.find((usageDetailStatus) => usageDetailStatus.status === value)
      ?.formattedStatus;
  };

  const commonColumnConfig = {
    disableRowGroup: true,
  };

  const headerColumns: Column<unknown>[] = [
    {
      caption: 'Uploaded on',
      name: 'created',
      cellType: CellType.Date,
      width: 170,
      defaultFilter: 1,
      cellFormatter: CellFormatters.dateAndTime,
      ...commonColumnConfig,
    },
    {
      caption: 'File name',
      name: 'fileName',
      width: 250,
      cellType: CellType.String,
      defaultFilter: 2,
      cellRenderer: createLinkCellRenderer<UsageHeaderOutput>(({ data, searchString }) => {
        const documentId = data?.iD ?? '';
        if (!documentId) return '#';
        return goToUsageRecords(documentId, searchString);
      }),
      onCellClicked: (e: CellClickedEvent<unknown>) => {
        if (shouldSkipCellClickNavigation(e)) return;
        const data = e.data as UsageHeaderOutput | undefined;
        const documentId = data?.iD ?? '';
        const searchStringIfAny = e.context?.serverSide?.current?.search;
        if (documentId) {
          browserHistory.push(goToUsageRecords(documentId, searchStringIfAny));
        }
      },
      ...commonColumnConfig,
    },
    {
      caption: 'Uploaded by',
      name: 'uploadedBy',
      width: 170,
      cellType: CellType.String,
      sortable: false,
      filterType: FilterValueTypes.StringMultiSelectFilter,
      filterOptions: {
        availableOptions: [StringFilterOption.ContainsValues],
      },
      renderFilter: (renderProps) => renderUploadedBy({ ...renderProps, title: 'Uploaded by' }),
      defaultFilter: 3,
      ...commonColumnConfig,
    },
    {
      caption: 'Status',
      name: 'status',
      width: 170,
      cellType: CellType.String,
      filterType: FilterValueTypes.EnumFilter,
      filterData: DataMappers.usageHeaderStatusesToOptionsArray(usageHeaderStatuses).map(option => ({
        label: option.label,
        value: option.value != null ? String(option.value) : '',
      })),
      defaultFilter: 4,
      cellFormatter: ({ data }: ValueFormatterParams<unknown>) => {
        if (data && typeof data === 'object' && 'status' in data) {
          const formatted = mapEnumToFormattedStatus((data as { status?: VarBillingUsageHeaderStatusEnum }).status);
          return formatted ?? '';
        }
        return '';
      },
      ...commonColumnConfig,
    },
    {
      caption: 'Number of rows',
      name: 'numberOfRows',
      width: 170,
      cellType: CellType.Integer,
      defaultFilter: 5,
      ...commonColumnConfig,
    },
    {
      caption: 'Product name',
      name: 'productName',
      width: 200,
      cellType: CellType.String,
      defaultFilter: 6,
      ...commonColumnConfig,
    },
    {
      name: 'totalNet',
      caption: 'Imported Total Value',
      width: 200,
      cellStyle: { justifyContent: 'end' },
      cellType: CellType.String,
      cellFormatter: (params: ValueFormatterParams) => CellFormatters.decimal(params),
      defaultFilter: 7,
      filterType: FilterValueTypes.StringFilter,
      filterOptions: {
        availableOptions: filtersforStringifiedNumbers,
      },
      ...commonColumnConfig,
    },
    {
      name: 'calculatedTotalValue',
      caption: 'Calculated Total Value',
      width: 200,
      cellStyle: { justifyContent: 'end' },
      cellType: CellType.String,
      cellFormatter: (params: ValueFormatterParams) => CellFormatters.decimal(params),
      defaultFilter: 8,
      filterType: FilterValueTypes.StringFilter,
      filterOptions: {
        availableOptions: filtersforStringifiedNumbers,
      },
      ...commonColumnConfig,
    },
    {
      name: 'iD',
      caption: 'ID',
      width: 270,
      cellType: CellType.String,
      defaultFilter: 32,
      hidden: true,
      filterType: FilterValueTypes.StringFilter,
      filterOptions: {
        availableOptions: guidFilterOptions,
      },
    },
  ];

  return useMemo(() => ({ headerColumns }), []);
};
